/**
 * Pressure-aware moist-air psychrometrics for the NREL TMY ingestion boundary.
 *
 * Equations follow ASHRAE Handbook—Fundamentals (2021), Chapter 1:
 * saturation pressure (5/6), perfect-gas humidity ratio (21/23), and the
 * thermodynamic wet-bulb adiabatic-saturation balance (33, rearranged as
 * 35/37). The bisection solves that balance; it is not the pressure-free
 * empirical Stull approximation.
 *
 * Source: https://handbook.ashrae.org/Handbooks/F21/SI/F21_Ch01/F21_Ch01_si.aspx
 */

const WATER_TO_DRY_AIR_MOLECULAR_MASS_RATIO = 0.621_945;
const MIN_ASHRAE_TEMPERATURE_C = -100;
const MAX_TMY_DRY_BULB_C = 80;
const CELSIUS_TO_KELVIN = 273.15;
const PASCALS_PER_MEGAPASCAL = 1_000_000;
const MAX_BISECTION_ITERATIONS = 100;
const WET_BULB_TOLERANCE_C = 1e-7;

export interface MoistAirState {
  dryBulbC: number;
  relativeHumidity: number; // 0..1
  pressureMPa: number; // absolute
}

function assertFinite(value: number, field: string): void {
  if (!Number.isFinite(value)) {
    throw new Error(`${field} must be finite`);
  }
}

function assertTemperatureInAshraeRange(temperatureC: number, field: string): void {
  assertFinite(temperatureC, field);
  if (temperatureC < MIN_ASHRAE_TEMPERATURE_C || temperatureC > 200) {
    throw new Error(
      `${field} ${String(temperatureC)} C is outside the ASHRAE saturation-pressure range `
      + `(${String(MIN_ASHRAE_TEMPERATURE_C)} to 200 C)`,
    );
  }
}

/** ASHRAE equations 5/6; returns pure-water saturation vapor pressure in Pa. */
export function saturationVaporPressurePa(temperatureC: number): number {
  assertTemperatureInAshraeRange(temperatureC, 'temperature');
  const temperatureK = temperatureC + CELSIUS_TO_KELVIN;
  const lnPressure = temperatureC <= 0
    ? (-5.674_535_9e3 / temperatureK)
      + 6.392_524_7
      - (9.677_843e-3 * temperatureK)
      + (6.221_570_1e-7 * temperatureK ** 2)
      + (2.074_782_5e-9 * temperatureK ** 3)
      - (9.484_024e-13 * temperatureK ** 4)
      + (4.163_501_9 * Math.log(temperatureK))
    : (-5.800_220_6e3 / temperatureK)
      + 1.391_499_3
      - (4.864_023_9e-2 * temperatureK)
      + (4.176_476_8e-5 * temperatureK ** 2)
      - (1.445_209_3e-8 * temperatureK ** 3)
      + (6.545_967_3 * Math.log(temperatureK));
  return Math.exp(lnPressure);
}

function humidityRatioFromVaporPressure(
  vaporPressurePa: number,
  totalPressurePa: number,
): number {
  if (vaporPressurePa < 0 || vaporPressurePa >= totalPressurePa) {
    throw new Error(
      `Water-vapor pressure ${String(vaporPressurePa)} Pa must be non-negative and below `
      + `total pressure ${String(totalPressurePa)} Pa`,
    );
  }
  return WATER_TO_DRY_AIR_MOLECULAR_MASS_RATIO
    * vaporPressurePa
    / (totalPressurePa - vaporPressurePa);
}

/** ASHRAE equations 21/23 under the Handbook's perfect-gas approximation. */
export function humidityRatioFromRelativeHumidity(state: MoistAirState): number {
  const { dryBulbC, relativeHumidity, pressureMPa } = state;
  assertTemperatureInAshraeRange(dryBulbC, 'dryBulbC');
  assertFinite(relativeHumidity, 'relativeHumidity');
  assertFinite(pressureMPa, 'pressureMPa');
  if (relativeHumidity < 0 || relativeHumidity > 1) {
    throw new Error(`relativeHumidity ${String(relativeHumidity)} must be between 0 and 1`);
  }
  if (pressureMPa <= 0) {
    throw new Error(`pressureMPa ${String(pressureMPa)} must be positive absolute pressure`);
  }
  const pressurePa = pressureMPa * PASCALS_PER_MEGAPASCAL;
  return humidityRatioFromVaporPressure(
    relativeHumidity * saturationVaporPressurePa(dryBulbC),
    pressurePa,
  );
}

function saturationHumidityRatio(temperatureC: number, pressurePa: number): number {
  return humidityRatioFromVaporPressure(
    saturationVaporPressurePa(temperatureC),
    pressurePa,
  );
}

/** ASHRAE equations 35/37: humidity ratio implied by dry- and wet-bulb temperatures. */
export function humidityRatioFromThermodynamicWetBulb(
  dryBulbC: number,
  wetBulbC: number,
  pressureMPa: number,
): number {
  assertTemperatureInAshraeRange(dryBulbC, 'dryBulbC');
  assertTemperatureInAshraeRange(wetBulbC, 'wetBulbC');
  assertFinite(pressureMPa, 'pressureMPa');
  if (wetBulbC > dryBulbC) {
    throw new Error('wetBulbC cannot exceed dryBulbC');
  }
  if (pressureMPa <= 0) {
    throw new Error(`pressureMPa ${String(pressureMPa)} must be positive absolute pressure`);
  }

  const pressurePa = pressureMPa * PASCALS_PER_MEGAPASCAL;
  const saturatedRatio = saturationHumidityRatio(wetBulbC, pressurePa);
  if (wetBulbC >= 0) {
    return (
      ((2_501 - (2.326 * wetBulbC)) * saturatedRatio)
      - (1.006 * (dryBulbC - wetBulbC))
    ) / (2_501 + (1.86 * dryBulbC) - (4.186 * wetBulbC));
  }
  return (
    ((2_830 - (0.24 * wetBulbC)) * saturatedRatio)
    - (1.006 * (dryBulbC - wetBulbC))
  ) / (2_830 + (1.86 * dryBulbC) - (2.1 * wetBulbC));
}

/**
 * Solve thermodynamic wet bulb from dry bulb, relative humidity, and absolute
 * pressure. The result satisfies the ASHRAE adiabatic-saturation water/mass
 * and moist-air enthalpy balance at the supplied pressure.
 */
export function calculateThermodynamicWetBulbC(state: MoistAirState): number {
  const { dryBulbC, relativeHumidity, pressureMPa } = state;
  if (dryBulbC > MAX_TMY_DRY_BULB_C) {
    throw new Error(
      `dryBulbC ${String(dryBulbC)} C exceeds the ${String(MAX_TMY_DRY_BULB_C)} C `
      + 'TMY ingestion guard',
    );
  }
  const targetHumidityRatio = humidityRatioFromRelativeHumidity(state);
  if (relativeHumidity === 1) return dryBulbC;

  let lowerC = MIN_ASHRAE_TEMPERATURE_C;
  let upperC = dryBulbC;
  const lowerResidual = humidityRatioFromThermodynamicWetBulb(
    dryBulbC,
    lowerC,
    pressureMPa,
  ) - targetHumidityRatio;
  const upperResidual = humidityRatioFromThermodynamicWetBulb(
    dryBulbC,
    upperC,
    pressureMPa,
  ) - targetHumidityRatio;

  if (lowerResidual > 0 || upperResidual < 0) {
    throw new Error(
      `Unable to bracket thermodynamic wet bulb for ${String(dryBulbC)} C, `
      + `${String(relativeHumidity)} RH, ${String(pressureMPa)} MPa`,
    );
  }

  for (let iteration = 0; iteration < MAX_BISECTION_ITERATIONS; iteration += 1) {
    const midpointC = (lowerC + upperC) / 2;
    const midpointResidual = humidityRatioFromThermodynamicWetBulb(
      dryBulbC,
      midpointC,
      pressureMPa,
    ) - targetHumidityRatio;
    if (Math.abs(midpointResidual) < Number.EPSILON
      || (upperC - lowerC) <= WET_BULB_TOLERANCE_C) {
      return midpointC;
    }
    if (midpointResidual > 0) {
      upperC = midpointC;
    } else {
      lowerC = midpointC;
    }
  }

  // The fixed iteration bound is well beyond the tolerance requirement; this
  // return is deterministic even if floating-point residuals stop shrinking.
  return (lowerC + upperC) / 2;
}
